import { animate, transition, trigger, style } from '@angular/animations';
import { ChatContentService } from '../chat-content.service';
import { ChatDisclaimerComponent } from './chat-disclaimer/chat-disclaimer.component';
import { ChatOptionsComponent } from './chat-options/chat-options.component';
import { ChatValidationQuestionsComponent } from './chat-validation-questions/chat-validation-questions.component';
import { Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { IChatOption, ITaxIdAndName } from '../chat.models';
import { interval, Observable } from 'rxjs';
import { PROMPT_TEXTS } from '../chat.constants';
import { DateTime } from 'luxon';
import { Helpers } from 'src/app/utils/helpers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const elementHidden = { opacity: 0 };
const elementShown = { opacity: 1 };

@Component({
  selector: 'app-chat-questions',
  standalone: true,
  imports: [
    ChatDisclaimerComponent,
    ChatOptionsComponent,
    ChatValidationQuestionsComponent
  ],
  templateUrl: './chat-questions.component.html',
  styleUrl: './chat-questions.component.scss',
  animations: [
    trigger('showHide', [
      transition(':enter', [
        style(elementHidden),
        animate('0s', style(elementShown))
      ]),
      transition(':leave', [
        style(elementShown),
        animate('300ms', style(elementHidden))
      ])
    ])
  ]
})
export class ChatQuestionsComponent implements OnInit {
  private readonly _chatContentService = inject(ChatContentService);

  readonly cancelChatSelected = output<void>();
  readonly chatTokenAcquired = output<string>();

  protected readonly chatOptions_ = signal<IChatOption[]>([]);
  protected readonly isLoading_ = signal(false);
  protected readonly promptText_ = signal('');
  protected readonly teamSelection_ = signal('');
  protected securitySettings: ITaxIdAndName[] = null;
  protected readonly showChatOptions_ = signal<boolean>(true);
  protected readonly showDisclaimer_ = signal(true);
  private readonly MINUTE = 1000 * 60;

  private readonly timer$ = interval(this.MINUTE).pipe(
    takeUntilDestroyed(),
    tap(() => this.chatOptions_.set(Helpers.copy(this.chatOptions_())))
  );

  protected readonly filteredChatOptions_ = computed(() => {
    let options = this.chatOptions_();

    options = options.filter(i => {
      const now = DateTime.local({ zone: 'America/New_York' });
      const clinOpsEndTime = DateTime.local({ zone: 'America/New_York' }).set({ hour: 16, minute: 30 });

      // check for clinical ops and if it's after 4:30 PM EST
      if (i.key === 'PNG-CO' && now >= clinOpsEndTime) {
        return false;
      }

      return true;
    }).map((value, index) => {
      const copy = Helpers.copy(value);

      // reset ids so they start at zero
      copy.id = index;

      return copy;
    });

    return options;
  });

  ngOnInit(): void {
    if (!this.chatOptions_().length) {
      this._initializeChatContent().subscribe();

      this._getChatSecuritySettings().subscribe();
    }

    this.timer$.subscribe();
  }

  private _determinePromptText(chatOptions: IChatOption[]): string {
    const isNonBasicUser = chatOptions.some(chatOption => !!chatOption.isTeamSelection);

    return isNonBasicUser ? PROMPT_TEXTS.SUPPORT_TEAM_SELECTION : PROMPT_TEXTS.TOPIC_SELECTION;
  }

  private _getChatSecuritySettings(): Observable<ITaxIdAndName[]> {
    return this._chatContentService.getChatSecuritySettings()
      .pipe(
        map((res) => res.taxIdAndNames),
        tap((res) => this.securitySettings = res)
      )
  }

  private _initializeChatContent(): Observable<IChatOption[]> {
    this.isLoading_.set(true);

    return this._chatContentService.getChatTopics()
      .pipe(
        tap(res => {
          const prompText = this._determinePromptText(res);

          this.promptText_.set(prompText);

          this.chatOptions_.set(res);
        }),
        finalize(() => this.isLoading_.set(false))
      );
  }

  protected handleBeginChatSelected(): void {
    this.showDisclaimer_.set(false);
  }

  protected handleCancelChatSelected(): void {
    this.cancelChatSelected.emit();
  }

  protected handleChatTokenAcquired(chatToken: string): void {
    this.chatTokenAcquired.emit(chatToken);
  }

  protected handleTeamSelection(event: { key: string, name: string }) {
    const { key, name } = event;

    this.isLoading_.set(true);

    this.teamSelection_.set(name);

    this._chatContentService.postChatSelectionKey(key)
      .pipe(
        filter((res) => !!res),
        switchMap(() => this._chatContentService.getChatSubTopics()),
        tap((res) => {
          this.promptText_.set(PROMPT_TEXTS.TOPIC_SELECTION);
          this.chatOptions_.set(res);
        }),
        finalize(() => this.isLoading_.set(false))
      )
      .subscribe();
  }

  protected handleTopicSelected(event: { key: string, isSubTopic: boolean }): void {
    const { key, isSubTopic } = event;

    this.isLoading_.set(true);

    // We only want to do a post call to the setselectedchatoption Api once
    // If this is a sub-topic, that means we already made the call when we retrieved the sub-topics
    if (isSubTopic) {
      this.showChatOptions_.set(false);
      this.isLoading_.set(false);

      return;
    }

    this._chatContentService.postChatSelectionKey(key)
      .pipe(
        filter((res) => !!res),
        tap(() => this.showChatOptions_.set(false)),
        finalize(() => this.isLoading_.set(false))
      )
      .subscribe();
  }
}
